import { FileItem } from '@/app/(dashboard)/components/FileItem'
import { useUploadVersion } from '@/app/(dashboard)/upload-version/UploadVersionProvider'
import {
  Button,
  Icon,
  IconColor,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  BaseAlert,
  Spinner,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import { UploadDocumentParams } from '@/dc-shared-web/src/hooks/api/document/uploadDocument'
import {
  faArrowRightArrowLeft,
  faExclamationCircle,
} from '@document-crunch/pro-regular-svg-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { step2Schema } from '@/app/(dashboard)/upload-version/step2/Step2Schema'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'

export type Step2Props = {
  errorMessage?: string
  onUpload: (params: UploadDocumentParams) => Promise<void>
  solutionTypeUuid: string | undefined
}

export const Step2: FC<Step2Props> = ({
  errorMessage,
  onUpload,
  solutionTypeUuid,
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const { nextStep, prevStep, document, stepsData, updateStepsData } =
    useUploadVersion()
  const { trackEvent } = useFSTrackEvent()

  const file = stepsData.step1.file

  const form = useForm<z.infer<typeof step2Schema>>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      name: file?.name || '',
    },
  })

  const onFileRemove = () => {
    form.setValue('name', '')
    form.trigger('name')

    updateStepsData('step1', {
      file: undefined,
    })

    prevStep()
  }

  const onSubmit = async () => {
    if (!form.formState.isValid || !file || !solutionTypeUuid) return

    trackEvent(FS.BUTTON_CLICKED, {
      name: 'Step 2',
      page_category: FS_CATEGORY.VERSION_COMPARISON,
      entity_id: document.uuid,
      entity_type: 'crunch',
    })

    const data = form.getValues()

    updateStepsData('step2', {
      fileName: data.name,
    })

    const renamedFile = new File([file], data.name, { type: file.type })

    const uploadParams: UploadDocumentParams = {
      file: renamedFile,
      crunchUuid: document.uuid,
      templateUuid: solutionTypeUuid,
    }

    try {
      setIsUploading(true)

      await onUpload(uploadParams)

      nextStep()
    } catch (error) {
      /* Error displayed via errorMessage prop */
    } finally {
      setIsUploading(false)
    }
  }

  const isFormValid = form.formState.isValid

  return (
    <div className='tw-space-y-4'>
      <p>Comparing 2 versions of the same document:</p>
      <div className='tw-space-y-4'>
        <div className='tw-space-y-2'>
          <p className='tw-font-medium tw-text-sm'>Original document</p>
          <p className='tw-font-medium'>{document.fileName}</p>
        </div>
        <div className='tw-space-y-2'>
          <p className='tw-font-medium tw-text-sm'>New version</p>
          {file && (
            <FileItem
              file={file}
              onRemove={onFileRemove}
              errorMessage=''
              isLoading={isUploading}
            />
          )}
        </div>
        <Form {...form}>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel required>File name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    data-testid='file-name-input'
                    placeholder='Enter file name'
                    size='lg'
                    autoFocus
                    onChange={(e) => {
                      form.setValue('name', e.target.value)
                      form.trigger('name')
                    }}
                    autoComplete='off'
                    disabled={isUploading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Form>
        {errorMessage && (
          <BaseAlert
            variant='destructive'
            title='File failed to upload'
            description={errorMessage}
            icon={faExclamationCircle}
          />
        )}
      </div>
      <div className='tw-flex tw-items-center tw-justify-end tw-gap-2'>
        <Button
          onClick={() => {
            trackEvent(FS.BUTTON_CLICKED, {
              name: 'Back',
              page_category: FS_CATEGORY.VERSION_COMPARISON,
              entity_id: document.uuid,
              entity_type: 'crunch',
            })
            prevStep()
          }}
          variant='outline'
          data-testid='back-button'
          disabled={isUploading}
        >
          Back
        </Button>
        <Button
          disabled={!isFormValid || isUploading}
          onClick={() => form.handleSubmit(onSubmit)()}
          type='button'
          data-testid='compare-versions-btn'
        >
          <div className='tw-flex tw-items-center tw-gap-2'>
            {isUploading ? (
              <Spinner className='tw-text-white tw-border-2 tw-w-4 tw-h-4' />
            ) : (
              <Icon
                icon={faArrowRightArrowLeft}
                size='1x'
                color={IconColor.white}
              />
            )}
            <span>{isUploading ? 'Uploading' : 'Compare versions'}</span>
          </div>
        </Button>
      </div>
    </div>
  )
}
